// LICENSE_CODE ZON
'use strict'; /*jslint browser:true, es6:true, node:true, react:true*/
import React, {useRef, useState, useEffect, useCallback, useMemo,
    useLayoutEffect} from 'react';
import cn from 'classnames';
import styles from './select.module.less';

export interface i_basic_select_option {
    value: string | number;
    label: string;
    disabled?: boolean;
}

export interface i_render_body_props {
    selected: boolean;
    opened: boolean;
    value: string | number | null;
    focused: boolean;
    disabled: boolean;
}

export interface i_render_option_props {
    option: i_basic_select_option;
    selected: boolean;
    highlighted: boolean;
    focused: boolean;
}

export interface i_basic_select_props {
    value?: string | number | null;
    default_value?: string | number | null;
    options: Array<i_basic_select_option>;

    on_change?: (value: string | number | null)=>void;
    on_open?: ()=>void;
    on_close?: ()=>void;
    on_focus?: (e: React.FocusEvent)=>void;
    on_blur?: (e: React.FocusEvent)=>void;

    render_body: (props: i_render_body_props)=>React.ReactNode;
    render_option: (props: i_render_option_props)=>React.ReactNode;
    render_label?: ()=>React.ReactNode;
    render_empty?: ()=>React.ReactNode;

    cls?: string;
    disabled?: boolean;
    tab_index?: number;
}

const use_select = (props: i_basic_select_props)=>{
    const {
        value: controlled_value,
        default_value = null,
        options = [],
        on_change,
        on_open,
        on_close,
        disabled = false,
    } = props;

    const [is_open, set_is_open] = useState(false);
    const [internal_value, set_internal_value] =
        useState<string | number | null>(default_value);
    const [focused, set_focused] = useState(false);
    const [highlighted_idx, set_highlighted_idx] = useState(-1);
    const value = controlled_value !== undefined ? controlled_value
        : internal_value;
    const has_selected_value = value !== null;

    const toggle_dropdown = useCallback((state?: boolean)=>{
        if (disabled)
            return;
        set_is_open(current=>{
            const new_state = state ?? !current;
            if (new_state)
            {
                if (on_open)
                    on_open();
            }
            else if (on_close)
                on_close();
            return new_state;
        });
    }, [disabled, on_open, on_close]);

    const handle_option_select = useCallback((option: i_basic_select_option)=>{
        if (option.disabled)
            return;
        const new_value = value == option.value ? null : option.value;
        if (controlled_value === undefined)
            set_internal_value(new_value);
        if (on_change)
            on_change(new_value);
        set_is_open(false);
        if (on_close)
            on_close();
    }, [value, controlled_value, on_change, on_close]);

    const search_query = useRef('');
    const search_timeout = useRef<number>();

    useEffect(()=>{
        return ()=>{
            if (search_timeout.current)
                window.clearTimeout(search_timeout.current);
        };
    }, []);

    const filtered_options = useMemo(()=>
        options.filter(opt=>!opt.disabled),
    [options]);

    const handle_key_down = useCallback((e: React.KeyboardEvent)=>{
        if (disabled)
            return;
        switch (e.key)
        {
            case 'ArrowDown':
                e.preventDefault();
                if (!is_open)
                    set_is_open(true);
                else
                {
                    set_highlighted_idx(prev=>
                        prev < filtered_options.length - 1 ? prev + 1 : 0);
                }
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (!is_open)
                    set_is_open(true);
                else
                {
                    set_highlighted_idx(prev=>
                        prev > 0 ? prev - 1 : filtered_options.length - 1);
                }
                break;
            case 'Enter':
                e.preventDefault();
                if (is_open && highlighted_idx >= 0)
                    handle_option_select(filtered_options[highlighted_idx]);
                else
                    toggle_dropdown();
                break;
            case 'Escape':
                e.preventDefault();
                if (is_open)
                {
                    set_is_open(false);
                    if (on_close)
                        on_close();
                }
                break;
            default:
                if (e.key.length == 1 && !e.ctrlKey && !e.altKey && !e.metaKey)
                {
                    const new_query = search_query.current
                        + e.key.toLowerCase();
                    if (new_query.length == 1)
                    {
                        const next_idx = filtered_options.findIndex(
                            (opt, idx)=>idx > highlighted_idx &&
                                opt.label.toLowerCase().startsWith(new_query));
                        if (next_idx >= 0)
                            set_highlighted_idx(next_idx);
                        else
                        {
                            const first_idx = filtered_options.findIndex(opt=>
                                opt.label.toLowerCase().startsWith(new_query));
                            if (first_idx >= 0)
                                set_highlighted_idx(first_idx);
                        }
                    }
                    else
                    {
                        const first_idx = filtered_options.findIndex(opt=>
                            opt.label.toLowerCase().startsWith(new_query));
                        if (first_idx >= 0)
                            set_highlighted_idx(first_idx);
                    }
                    search_query.current = new_query;
                    if (search_timeout.current)
                        window.clearTimeout(search_timeout.current);
                    search_timeout.current = window.setTimeout(()=>{
                        search_query.current = '';
                    }, 500);
                }
                break;
        }
    }, [disabled, is_open, highlighted_idx, filtered_options,
        handle_option_select, on_close, toggle_dropdown]);

    useEffect(()=>{
        if (is_open)
        {
            if (value !== null)
            {
                const selected_idx = filtered_options
                    .findIndex(opt=>opt.value == value);
                if (selected_idx >= 0)
                    set_highlighted_idx(selected_idx);
                else if (filtered_options.length)
                    set_highlighted_idx(0);
            }
            else if (filtered_options.length)
                set_highlighted_idx(0);
        }
        else
            set_highlighted_idx(-1);
    }, [is_open, filtered_options, value]);

    return {
        is_open,
        focused,
        highlighted_idx,
        value,
        has_selected_value,
        toggle_dropdown,
        handle_option_select,
        handle_key_down,
        set_focused,
    };
};

export const Basic_select = (props: i_basic_select_props)=>{
    const {
        options = [],
        on_focus,
        on_blur,
        render_body,
        render_option,
        render_label,
        render_empty,
        cls,
        disabled = false,
        tab_index = 0,
    } = props;

    const {
        is_open,
        focused,
        highlighted_idx,
        value,
        has_selected_value,
        toggle_dropdown,
        handle_option_select,
        handle_key_down,
        set_focused,
    } = use_select(props);

    const container_ref = useRef<HTMLDivElement>(null);
    const list_ref = useRef<HTMLDivElement>(null);
    const dropdown_ref = useRef<HTMLDivElement>(null);
    const option_refs = useRef<{[key: string]: HTMLDivElement | null}>({});

    useEffect(()=>{
        const current_keys = options.map(opt=>String(opt.value));
        Object.keys(option_refs.current).forEach(key=>{
            if (!current_keys.includes(key))
                delete option_refs.current[key];
        });
    }, [options]);

    useEffect(()=>{
        if (!is_open || highlighted_idx < 0 || !options[highlighted_idx])
            return;
        const option_key = String(options[highlighted_idx].value);
        const option_element = option_refs.current[option_key];
        const list_element = list_ref.current;
        if (option_element && list_element)
        {
            const list_rect = list_element.getBoundingClientRect();
            const option_rect = option_element.getBoundingClientRect();
            const is_above_visible = option_rect.top < list_rect.top;
            const is_below_visible = option_rect.bottom > list_rect.bottom;
            if (is_above_visible || is_below_visible)
                option_element.scrollIntoView({block: 'nearest'});
        }
    }, [is_open, highlighted_idx, options]);

    useEffect(()=>{
        if (!is_open)
            return;
        const handle_outside_click = (e: MouseEvent)=>{
            if (container_ref.current &&
                !container_ref.current.contains(e.target as Node))
            {
                toggle_dropdown();
            }
        };
        document.addEventListener('mousedown', handle_outside_click);
        return ()=>{
            document.removeEventListener('mousedown', handle_outside_click);
        };
    }, [is_open, toggle_dropdown]);

    useLayoutEffect(()=>{
        if (!is_open || !container_ref.current || !dropdown_ref.current)
            return;
        const container = container_ref.current;
        const dropdown = dropdown_ref.current;
        const {bottom} = container.getBoundingClientRect();
        const viewport_height = window.innerHeight;
        const dropdown_height = dropdown.getBoundingClientRect().height;
        const space_below = viewport_height - bottom;
        if (space_below < dropdown_height)
        {
            const shift = dropdown_height - space_below;
            dropdown.style.transform = `translateY(-${shift+8}px)`;
        }
        else
            dropdown.style.transform = '';
    }, [is_open]);

    const handle_focus = useCallback((e: React.FocusEvent)=>{
        set_focused(true);
        if (on_focus)
            on_focus(e);
    }, [on_focus, set_focused]);

    const handle_blur = useCallback((e: React.FocusEvent)=>{
        set_focused(false);
        queueMicrotask(()=>{
            if (!container_ref.current?.contains(document.activeElement))
            {
                toggle_dropdown(false);
                if (on_blur)
                    on_blur(e);
            }
        });
    }, [on_blur, toggle_dropdown]);

    const handle_click = useCallback(()=>{
        toggle_dropdown();
    }, [toggle_dropdown]);

    const tab_index_value = disabled ? undefined : tab_index;

    const body_content = render_body({
        selected: has_selected_value,
        opened: is_open,
        value,
        focused,
        disabled,
    });

    const options_list = useMemo(()=>{
        if (!options.length)
        {
            return render_empty ? <div data-elem="list" data-state="empty">
              {render_empty()}
            </div> : null;
        }
        return <div ref={list_ref} data-elem="list">
          {options.map((option, idx)=>{
            const is_selected = option.value == value;
            const is_highlighted = idx == highlighted_idx;
            const option_content = render_option({
              option,
              selected: is_selected,
              highlighted: is_highlighted,
              focused: focused && is_highlighted,
            });
            return <div
              key={String(option.value)}
              ref={el=>option_refs.current[String(option.value)] = el}
              onClick={()=>handle_option_select(option)}
              data-elem="option"
              data-state-selected={`${is_selected}`}
              data-state-highlighted={`${is_highlighted}`}
              data-state-disabled={`${!!option.disabled}`}
              data-state-focused={`${focused && is_highlighted}`}>
              {option_content}
            </div>;
          })}
        </div>;
    }, [options, value, highlighted_idx, focused, render_option,
        render_empty, handle_option_select]);

    const root_cls = cn(styles.select_container, cls);
    return <div
      ref={container_ref}
      className={root_cls}
      tabIndex={tab_index_value}
      onFocus={handle_focus}
      onBlur={handle_blur}
      onKeyDown={handle_key_down}
      data-elem="select"
      data-state-focused={`${focused}`}
      data-state-disabled={`${disabled}`}
      data-state-opened={`${is_open}`}
      data-state-empty={`${!has_selected_value}`}
      role="combobox"
      aria-expanded={`${is_open}`}
    >
      {render_label && <div data-elem="label">
        {render_label()}
      </div>}
      <div onClick={handle_click} data-elem="body">{body_content}</div>
      <div ref={dropdown_ref} data-elem="dropdown" role="listbox">
        {options_list}
      </div>
    </div>;
};
